import { getApiErrorMessage } from '@quoruum/frontend-common/api/errorMessage';
import { queryClient } from '@quoruum/frontend-common/api/queryClient';
import {
  hotelSettingGetSettingsOptions,
  hotelSettingGetSettingsQueryKey,
  hotelSettingSaveMutation,
} from '@quoruum/frontend-common/oas/gen/admin/@tanstack/react-query.gen';
import type {
  HotelSettingsBean,
  OptionTupleBeanWritable,
} from '@quoruum/frontend-common/oas/gen/admin/types.gen';
import {
  BOOKING_SITES,
  HOTEL_SETTINGS_OPTIONS,
  type BookingSite,
} from '@quoruum/frontend-common/oas/gen/enumValues.gen';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { ReactElement } from 'react';
import { Link } from 'react-router';
import type {
  EditableFieldSpec,
  EditableFieldValue,
  EntityDetailRow,
} from '../../components/EditableEntityTable';
import { EditableEntityTable } from '../../components/EditableEntityTable';

type EditableHotelSettingsFieldKey = 'optionsTuples';

type HotelSettingsOptionTuple = OptionTupleBeanWritable;

const HOTEL_SETTINGS_OPTION_TUPLE_VALUES = BOOKING_SITES.flatMap((site) =>
  HOTEL_SETTINGS_OPTIONS.map((option) => `${site} - ${option}`)
);

function getHotelSettingsOptionTupleLabel(tuple: HotelSettingsOptionTuple): string | undefined {
  if (!tuple.site || !tuple.option) {
    return undefined;
  }

  return `${tuple.site} - ${tuple.option}`;
}

function getHotelSettingsOptionTuple(value: string): HotelSettingsOptionTuple | undefined {
  const separatorIndex = value.indexOf(' - ');
  if (separatorIndex < 0) {
    return undefined;
  }

  const site = value.slice(0, separatorIndex);
  const option = value.slice(separatorIndex + 3);
  if (
    !BOOKING_SITES.includes(site as BookingSite) ||
    !HOTEL_SETTINGS_OPTIONS.includes(option as (typeof HOTEL_SETTINGS_OPTIONS)[number])
  ) {
    return undefined;
  }

  return {
    site: site as BookingSite,
    option: option as (typeof HOTEL_SETTINGS_OPTIONS)[number],
  };
}

function extractEnabledHotelOptionTuples(tuples?: HotelSettingsData['optionsTuples']): string[] {
  if (!tuples || tuples.length === 0) {
    return [];
  }

  return tuples
    .map(getHotelSettingsOptionTupleLabel)
    .filter((tuple): tuple is string => tuple !== undefined);
}

function buildHotelOptionTupleSpec(
  values: ReadonlyArray<string>,
  originalValues: ReadonlyArray<string>
): Array<HotelSettingsOptionTuple> | undefined {
  const selectedTuples = values
    .map(getHotelSettingsOptionTuple)
    .filter((tuple): tuple is HotelSettingsOptionTuple => tuple !== undefined);
  const selectedTupleKeys = new Set(
    selectedTuples.map((tuple) => getHotelSettingsOptionTupleLabel(tuple))
  );
  const removedTuples = originalValues
    .map(getHotelSettingsOptionTuple)
    .filter((tuple): tuple is HotelSettingsOptionTuple => tuple !== undefined)
    .filter((tuple) => !selectedTupleKeys.has(getHotelSettingsOptionTupleLabel(tuple)));

  if (selectedTuples.length === 0 && removedTuples.length === 0) {
    return undefined;
  }

  return [...selectedTuples, ...removedTuples.map((tuple) => ({ ...tuple, deleted: true }))];
}

type HotelSettingsData = HotelSettingsBean;

const HOTEL_SETTINGS_EDITABLE_FIELD_SPECS: ReadonlyMap<
  EditableHotelSettingsFieldKey,
  EditableFieldSpec
> = new Map([
  [
    'optionsTuples',
    {
      editType: 'multiselect-enum',
      options: HOTEL_SETTINGS_OPTION_TUPLE_VALUES,
    },
  ],
]);

function buildHotelSettingsRows(
  settings: HotelSettingsData,
  selectedHotelId?: string
): Array<EntityDetailRow<EditableHotelSettingsFieldKey>> {
  const optionsTuples = extractEnabledHotelOptionTuples(settings.optionsTuples);

  return [
    {
      id: 'id',
      key: 'id',
      value: settings.id ?? selectedHotelId ?? '-',
    },
    {
      id: 'optionsTuples',
      key: 'options',
      value: optionsTuples.length > 0 ? optionsTuples.join(', ') : '-',
      editableFieldKey: 'optionsTuples',
      editableFieldArrayValue: optionsTuples,
    },
  ];
}

type HotelSettingsSectionProps = {
  selectedHotelId: string | undefined;
};

export function HotelSettingsSection({ selectedHotelId }: HotelSettingsSectionProps): ReactElement {
  const {
    data: selectedHotelSettingsResponse,
    error: selectedHotelSettingsError,
    isError: hasSelectedHotelSettingsError,
    isLoading: isSelectedHotelSettingsLoading,
  } = useQuery({
    ...(selectedHotelId
      ? hotelSettingGetSettingsOptions({
          path: {
            id: selectedHotelId,
          },
        })
      : hotelSettingGetSettingsOptions({
          path: {
            id: '',
          },
        })),
    enabled: Boolean(selectedHotelId),
    staleTime: 60 * 1000,
  });
  const saveHotelSettingsMutation = useMutation(hotelSettingSaveMutation());
  async function handleSaveHotelSettingsField(
    fieldKey: EditableHotelSettingsFieldKey,
    value: EditableFieldValue
  ): Promise<void> {
    if (!selectedHotelId) {
      return;
    }

    if (!Array.isArray(value)) {
      throw new Error('Invalid value for selected field.');
    }

    const selectedOptionTuples = value.filter(
      (item): item is string =>
        typeof item === 'string' && getHotelSettingsOptionTuple(item) !== undefined
    );

    if (selectedOptionTuples.length !== value.length) {
      throw new Error('Invalid hotel settings option tuples selected.');
    }

    const originalOptionTuples = extractEnabledHotelOptionTuples(
      selectedHotelSettingsResponse?.optionsTuples
    );
    const savedSettings = await saveHotelSettingsMutation.mutateAsync({
      path: {
        id: selectedHotelId,
      },
      body: {
        optionsTuples: buildHotelOptionTupleSpec(selectedOptionTuples, originalOptionTuples),
      },
    });

    queryClient.setQueryData(
      hotelSettingGetSettingsQueryKey({
        path: {
          id: selectedHotelId,
        },
      }),
      savedSettings
    );
  }

  return (
    <div className="min-h-0 flex-1 overflow-y-auto rounded-md border border-slate-200">
      {isSelectedHotelSettingsLoading ? (
        <p className="px-3 py-2 text-xs text-slate-600">Loading hotel settings...</p>
      ) : null}
      {hasSelectedHotelSettingsError ? (
        <p className="px-3 py-2 text-xs text-rose-700">
          {getApiErrorMessage(selectedHotelSettingsError, 'Unable to load hotel settings.')}
        </p>
      ) : null}
      {!isSelectedHotelSettingsLoading &&
      !hasSelectedHotelSettingsError &&
      selectedHotelSettingsResponse ? (
        <EditableEntityTable
          key={selectedHotelId}
          header={
            selectedHotelId ? (
              <div className="border-b border-slate-200 bg-slate-50 px-3 py-2 text-xs">
                <div className="flex items-center gap-3">
                  <Link
                    to={`/tools/changelog?entityType=HotelSettings&entityId=${encodeURIComponent(selectedHotelId)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-sky-700 underline"
                  >
                    Changelog
                  </Link>
                </div>
              </div>
            ) : null
          }
          rows={buildHotelSettingsRows(selectedHotelSettingsResponse, selectedHotelId)}
          fieldDefinitions={HOTEL_SETTINGS_EDITABLE_FIELD_SPECS}
          onSave={handleSaveHotelSettingsField}
        />
      ) : null}
      {!isSelectedHotelSettingsLoading &&
      !hasSelectedHotelSettingsError &&
      !selectedHotelSettingsResponse ? (
        <p className="px-3 py-2 text-xs text-slate-600">No hotel settings found.</p>
      ) : null}
    </div>
  );
}
